import { Slider } from '@mui/material';
import { Check } from '@phosphor-icons/react';
import { Button, Collapser, FilterButton, NavigationPills, VText } from '@vake/ui-kit';
import { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useDebounce } from '../../../../utils/debounce';
import { useOverwatchContext } from '../../interactiveReport/overwatch-2-context';
import { Calendar } from './calendar';

export const Filters: FC = () => {
    const {
        report: { subscriptionStart, subscriptionEnd },
        filters: {
            startDate,
            endDate,
            setStartDate,
            setEndDate,
            useDateInterval,
            darkDetectionConfidence,
            tags,
            setTags,
            setDarkDetectionConfidence,
            defaultConfidence,
            setVerifiedActive,
            setDarkActive,
            setUnCorrelatedActive,
            setWindmillsActive,
            setPlatformsActive,
            openConfidence,
            setOpenConfidence,
            openDate,
            setOpenDate,
            setOpenDetectionType,
            setOpenStaticObjects,
            setOpenTags,
            setTagFilterStrategy,
            openDetectionType,
            openStaticObjects,
            openTags,
            numDark,
            numPlatforms,
            numUncorrelated,
            numVerified,
            numWindmills,
            verifiedActive,
            unCorrelatedActive,
            darkActive,
            windmillsActive,
            platformsActive,
            tagFilterStrategy,
            sentinel1Active,
            sentinel2Active,
            setSentinel1Active,
            setSentinel2Active,
            openImageSources,
            setOpenImageSources,
        },
    } = useOverwatchContext();

    const [tempSliderValue, setTempSliderValue] = useState<number>(darkDetectionConfidence);
    const tomorrow = new Date();
    tomorrow.setDate(new Date().getDate() + 1);
    const updateTag = (index: number) => {
        const newTags = [...tags];
        newTags[index].subtle = !tags[index].subtle;
        setTags(newTags);
    };
    const resetTags = () => {
        setTags(
            tags.map((tag) => ({
                ...tag,
                subtle: true,
            }))
        );
    };
    useEffect(() => {
        setTempSliderValue(darkDetectionConfidence);
    }, [darkDetectionConfidence]);
    const updateDarkDetectionConfidenceParent = useDebounce(() => setDarkDetectionConfidence(tempSliderValue), 400);
    const handleSliderChange = (_: Event | SyntheticEvent, newValue: number | number[]) => {
        if (!Array.isArray(newValue)) {
            setTempSliderValue(newValue);
        }
        updateDarkDetectionConfidenceParent();
    };
    const resetConfidence = () => {
        setTempSliderValue(defaultConfidence);
        setDarkDetectionConfidence(defaultConfidence);
    };
    const resetDetectionType = () => {
        setVerifiedActive(false);
        setDarkActive(false);
        setUnCorrelatedActive(false);
    };
    const resetStaticObjects = () => {
        setWindmillsActive(false);
        setPlatformsActive(false);
    };
    const resetImageSources = () => {
        setSentinel1Active(false);
        setSentinel2Active(false);
    };
    const resetAllFilters = () => {
        resetConfidence();
        resetDetectionType();
        resetTags();
        resetStaticObjects();
        resetImageSources();
    };
    return (
        <div className="p-4">
            <div className="py-2">
                <Collapser
                    open={openDate}
                    setOpen={setOpenDate}
                    description={
                        useDateInterval ? <VText variant="H4">Date Interval</VText> : <VText variant="H4">Date</VText>
                    }
                >
                    <Calendar
                        subscriptionStart={subscriptionStart}
                        subscriptionEnd={subscriptionEnd}
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                        useDateInterval={useDateInterval}
                    />
                </Collapser>
            </div>
            <div className="py-2">
                <Collapser
                    open={openConfidence}
                    setOpen={setOpenConfidence}
                    description={<VText variant="H4">Dark Confidence</VText>}
                >
                    <div className="flex flex-wrap py-2 gap-2 ">
                        <Slider
                            value={tempSliderValue}
                            min={0}
                            max={100}
                            step={1}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => <div>{`${value}%`}</div>}
                            onChange={handleSliderChange}
                        />
                    </div>
                </Collapser>
            </div>
            <div className="py-2">
                <Collapser
                    open={openDetectionType}
                    setOpen={setOpenDetectionType}
                    description={<VText variant="H4">Detection Types</VText>}
                >
                    <div className="flex flex-wrap py-2 gap-2 ">
                        <FilterButton
                            customBackgroundColor="green"
                            subtle={!verifiedActive}
                            count={numVerified}
                            onClick={() => setVerifiedActive(!verifiedActive)}
                        >
                            Verified
                        </FilterButton>
                        <FilterButton
                            customBackgroundColor="red"
                            subtle={!darkActive}
                            count={numDark}
                            onClick={() => setDarkActive(!darkActive)}
                        >
                            Dark
                        </FilterButton>
                        <FilterButton
                            customBackgroundColor="blue"
                            subtle={!unCorrelatedActive}
                            count={numUncorrelated}
                            onClick={() => setUnCorrelatedActive(!unCorrelatedActive)}
                        >
                            Unverified
                        </FilterButton>
                    </div>
                </Collapser>
            </div>
            <div className="py-2">
                <Collapser
                    open={openStaticObjects}
                    setOpen={setOpenStaticObjects}
                    description={<VText variant="H4">Static Objects</VText>}
                >
                    <div className="flex flex-wrap py-2 gap-2 ">
                        <FilterButton
                            customBackgroundColor="cyan"
                            subtle={!windmillsActive}
                            count={numWindmills}
                            onClick={() => setWindmillsActive(!windmillsActive)}
                        >
                            Windmill
                        </FilterButton>
                        <FilterButton
                            customBackgroundColor="mint"
                            subtle={!platformsActive}
                            count={numPlatforms}
                            onClick={() => setPlatformsActive(!platformsActive)}
                        >
                            Platform
                        </FilterButton>
                    </div>
                </Collapser>
            </div>
            <div className="py-2">
                <Collapser
                    open={openImageSources}
                    setOpen={setOpenImageSources}
                    description={<VText variant="H4">Satellite Sources</VText>}
                >
                    <div className="flex flex-wrap py-2 gap-2 ">
                        <FilterButton
                            customBackgroundColor="orange"
                            subtle={!sentinel1Active}
                            onClick={() => setSentinel1Active(!sentinel1Active)}
                        >
                            Sentinel 1
                        </FilterButton>
                        <FilterButton
                            customBackgroundColor="indigo"
                            subtle={!sentinel2Active}
                            onClick={() => setSentinel2Active(!sentinel2Active)}
                        >
                            Sentinel 2
                        </FilterButton>
                    </div>
                </Collapser>
            </div>
            <div className="py-2">
                <Collapser
                    open={openTags}
                    setOpen={setOpenTags}
                    description={<VText variant="H4">Tags</VText>}
                    topRight={<Button text="Reset" variant="subtle" size="xsmall" onClick={resetTags} />}
                >
                    <div className="flex flex-wrap py-2 gap-2 ">
                        {tags &&
                            tags.map((tag, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        updateTag(index);
                                    }}
                                >
                                    <FilterButton {...tag} />
                                </button>
                            ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <VText variant="M">Require</VText>
                        <NavigationPills
                            options={['ANY', 'ALL']}
                            activeIndex={tagFilterStrategy === 'or' ? 0 : 1}
                            setActiveIndex={(index) => {
                                setTagFilterStrategy(index === 0 ? 'or' : 'and');
                            }}
                            size="xsmall"
                        />
                        <VText variant="M">tags to be present</VText>
                    </div>
                </Collapser>
            </div>
            <div className="">
                <Button
                    size="small"
                    variant="neutral"
                    text="Reset All Filters"
                    iconBefore={<Check size={16} />}
                    onClick={resetAllFilters}
                />
            </div>
        </div>
    );
};
